import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ConversionPermissions } from "@redotech/redo-model/permissions";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { memo } from "react";
import {
  CONVERSION_ICON,
  CONVERSION_ICON_FILLED,
  ConversionSuiteStatusesContext,
} from "../../../../redo-suite/conversion-cloud";
import { AbTestItems } from "./ab-tests";
import { AnalyticsItems } from "./analytics";
import { CheckoutItems } from "./checkouts";
import { DashboardItems } from "./dashboard";
import { OnboardingItems } from "./onboarding";
import { PostPurchaseItems } from "./post-purchase";

export function useConversionNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { checkoutOptimizationEnabled, checkoutOutboundRatesEnabled } =
    useRequiredContext(ConversionSuiteStatusesContext);

  const canViewConversion = useHasPermission(
    ConversionPermissions.ACCESS_CONVERSION,
  );

  if (team.platform !== "shopify" || !canViewConversion) {
    return null;
  }

  const pathRoot = routes.conversionBaseUrl;

  const primaryTab: LinkPrimaryTab = {
    id: "conversion",
    hasNotification: false,
    UnfilledIcon: CONVERSION_ICON,
    FilledIcon: CONVERSION_ICON_FILLED,
    type: "link",
    href: routes.getConversionHref({
      enabled: checkoutOptimizationEnabled,
      checkoutOutboundRatesEnabled,
    }),
    pathRoot,
    friendlyName: "Conversion",
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    checkoutOptimizationEnabled
      ? {
          id: "conversion",
          name: "Conversion",
          NavContent: SecondaryConversionSection,
        }
      : undefined;

  return { primaryTab, secondaryNavigationData };
}

const SecondaryConversionSection = memo(function SecondaryConversionSection() {
  return (
    <>
      <OnboardingItems />
      <DashboardItems />
      <AbTestItems />
      <CheckoutItems />
      <PostPurchaseItems />
      <AnalyticsItems />
    </>
  );
});
